import { formatDateOnly } from "@/lib/dateOnly";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { NavigationFailure, reasonFromError } from "@/components/NavigationFailure";
import { useDealerContext } from "@/lib/useDealerContext";
import { useCustomerView } from "@/context/CustomerViewContext";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Pencil, Ship, Warehouse, MapPin, FlaskConical, ShoppingBag, FolderPlus } from "lucide-react";
import { SampleRequestDialog } from "@/components/SampleRequestDialog";
import { OrderSheetDialog } from "@/components/OrderSheetDialog";
import { SaveToProjectDialog } from "@/components/SaveToProjectDialog";
import { ProductResources } from "@/components/ProductResources";
import { BackToTop } from "@/components/BackToTop";
import { NotifyMeButton, NotifyMeDialog, useMyNotifyMe } from "@/components/NotifyMe";
import { dimsLine } from "@/lib/dims";

const FORMAT_LABEL = { "4x4": '4×4', "2x6": "2×6 Bejmat", "2x2": "2×2" };

export default function DealerCatalogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { customerView } = useCustomerView();
  const [item, setItem] = useState(null);
  const [navFail, setNavFail] = useState(null);
  const [retailEdit, setRetailEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reserveTarget, setReserveTarget] = useState(null); // {container_id} | {florida:true}
  const [reserveBoxes, setReserveBoxes] = useState("");
  const [reserveProject, setReserveProject] = useState("");
  const [reserveLocationId, setReserveLocationId] = useState("");
  const { locations: dealerLocations, isManager, defaultLocationId, showLocationPicker } = useDealerContext();
  const [projects, setProjects] = useState([]);
  const [reserving, setReserving] = useState(false);
  const [sampleOpen, setSampleOpen] = useState(false);
  const [orderPrefill, setOrderPrefill] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [allVariants, setAllVariants] = useState([]);
  const [colorGridOpen, setColorGridOpen] = useState(false);
  const [colorQuery, setColorQuery] = useState("");
  const [availOpen, setAvailOpen] = useState(false);
  const notifyMe = useMyNotifyMe();

  const load = () => {
    api
      .get(`/catalog/${id}${customerView ? "?customer_view=1" : ""}`)
      .then((res) => setItem(res.data))
      .catch((e) => setNavFail(reasonFromError(e)));  // NAV-009
  };

  useEffect(load, [id, customerView]);
  useEffect(() => {
    api.get("/projects").then((res) => setProjects(res.data)).catch(() => {});
    api.get("/catalog").then((res) => setAllVariants(res.data || [])).catch(() => {});
  }, []);


  const handleReserve = async (e) => {
    e.preventDefault();
    setReserving(true);
    try {
      const payload = {
        variant_id: id,
        boxes: parseInt(reserveBoxes, 10),
        ...(reserveProject ? { project_id: reserveProject } : {}),
        // Layer 1: business location (project-linked reservations inherit the project's location)
        ...(!reserveProject && (reserveLocationId || defaultLocationId) && !isManager ? { location_id: reserveLocationId || defaultLocationId } : {}),
        ...(reserveTarget?.container_id ? { container_id: reserveTarget.container_id } : {}),
      };
      const { data } = await api.post("/reservations", payload);
      if (data.status === "PENDING_APPROVAL") {
        toast.info("Repeat hold — sent to Zelligio for approval. Stock is not held yet.");
      } else {
        toast.success(`Reserved ${data.boxes} boxes for ${data.hold_hours} hours`);
      }
      setReserveTarget(null);
      setReserveBoxes("");
      setReserveProject("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setReserving(false);
    }
  };

  const handleSaveRetail = async () => {
    setSaving(true);
    try {
      const body = item.box_coverage_sqft ? { retail_price_per_sqft: parseFloat(retailEdit) } : { retail_price: parseFloat(retailEdit) };
      try {
        await api.put(`/dealer/retail-prices/${id}`, body);
      } catch (e1) {
        const d = e1.response?.data?.detail;
        if (e1.response?.status === 409 && d?.code === "below_cost") {
          if (!window.confirm(`${d.message}\n\nWholesale cost: $${d.wholesale_cost_per_sqft}/sq ft · Your price: $${d.proposed_per_sqft}/sq ft · Margin: ${d.margin_pct}%\n\nKeep this price anyway?`)) throw e1;
          await api.put(`/dealer/retail-prices/${id}`, { ...body, acknowledge_below_cost: true });
        } else throw e1;
      }
      toast.success("Retail price updated");
      setRetailEdit(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (navFail) return <NavigationFailure reason={navFail} moduleLabel="Discover" modulePath="/dealer/discover" />;
  if (!item) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" data-testid="catalog-detail-loading">
        <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
      </div>
    );
  }

  const canTrade = !customerView && !item.quote_required;
  const formats = [
    { id: item.id, sku: item.sku, format_in: item.format_in, current: true },
    ...(item.siblings || []).map((s) => ({ ...s, current: false })),
  ];
  // Color × Format selector: switching color keeps the current format when possible
  const colorOptions = (() => {
    const byCode = new Map();
    for (const v of allVariants) {
      if (!v.color_code || v.category === "mosaic") continue;
      if (!byCode.has(v.color_code)) byCode.set(v.color_code, { code: v.color_code, name: v.color_name || v.color_code, byFormat: {}, image: null });
      const entry = byCode.get(v.color_code);
      entry.byFormat[v.format_in] = v.id;
      if (!entry.image && v.image_urls?.[0]) entry.image = v.image_urls[0];
    }
    return [...byCode.values()]
      .map((c) => ({ ...c, variant_id: c.byFormat[item.format_in] || Object.values(c.byFormat)[0] }))
      .sort((a, b) => a.code.localeCompare(b.code));
  })();

  return (
    <div data-testid="dealer-catalog-detail">
      <Button variant="ghost" onClick={() => navigate("/dealer/discover")} data-testid="back-to-discover" className="mb-6 -ml-3 gap-2 text-warmgrey hover:text-charcoal">
        <ArrowLeft className="h-4 w-4" /> Catalog
      </Button>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
        <div>
          {item.image_urls?.[0] ? (
            <img src={item.image_urls[0]} alt={item.label} className="aspect-square w-full rounded-md object-cover" />
          ) : (
            <div className="flex aspect-square items-center justify-center rounded-md bg-sand">
              <span className="font-display text-4xl font-light text-warmgrey">{item.category === "mosaic" ? (item.composition || []).map((c) => c.us_name || c.moroccan_name).join(" · ") : item.display_color_name || item.color_name}</span>
            </div>
          )}
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">{item.category === "mosaic" ? `Mosaic Collection${item.collection_family && item.collection_family !== "Mosaic" ? ` · ${item.collection_family}` : ""}` : item.product_type}</p>
          <h1 className="mt-2 font-display text-4xl font-light tracking-tight text-charcoal" data-testid="detail-title">{item.category === "mosaic" ? `${item.collection} — ${item.sku}` : item.label}</h1>
          {item.category === "mosaic" && (
            <div className="mt-3 rounded-md border border-border bg-white p-4" data-testid="detail-composition">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-warmgrey">Colors in this variation</p>
              <ul className="mt-2 space-y-1 text-sm text-charcoal">
                {(item.composition || []).map((c) => (
                  <li key={c.code} data-testid={`composition-color-${c.code}`}>{c.us_name || c.moroccan_name} <span className="text-xs text-muted-foreground">({c.code}{c.us_name ? ` · ${c.moroccan_name}` : ""}){c.share_pct ? ` · ${c.share_pct}%` : ""}</span></li>
                ))}
              </ul>
              {item.pattern && <p className="mt-2 text-xs text-muted-foreground">Pattern: {item.pattern}</p>}
              <Button variant="outline" size="sm" onClick={() => navigate(`/dealer/custom-studio?design_id=${encodeURIComponent(item.design_id)}&sku=${item.sku}`)} data-testid="detail-request-colors" className="mt-3 border-terracotta text-terracotta hover:bg-terracotta hover:text-white">Request a different color combination</Button>
            </div>
          )}

          {colorOptions.length > 1 && item.category !== "mosaic" && (
            <div className="mt-4" data-testid="color-selector">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-warmgrey" data-testid="color-name-line">
                Color — {item.display_color_name || item.color_name}
                {item.display_color_name && item.color_name && !String(item.product_title || "").includes(item.color_name) && <span className="ml-2 font-normal normal-case tracking-normal text-muted-foreground">Factory name: {item.color_name}</span>}
              </p>
              <div className="mt-2 flex items-center gap-2 overflow-x-auto pb-1">
                {colorOptions.map((c) => (
                  <button key={c.code} type="button" title={`${c.code} — ${c.name}`}
                    onClick={() => c.variant_id !== item.id && navigate(`/dealer/discover/${c.variant_id}`)}
                    data-testid={`color-tile-${c.code}`}
                    className={`relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-md border text-[10px] font-semibold transition-all duration-200 ${
                      c.code === item.color_code ? "border-terracotta ring-2 ring-terracotta/40" : "border-border hover:border-terracotta"
                    }`}>
                    {c.image ? (
                      <img src={c.image} alt={c.name} className="absolute inset-0 h-full w-full object-cover" />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center bg-sand text-charcoal">{c.code.replace("MZ", "")}</span>
                    )}
                    {c.code === item.color_code && c.image && <span className="absolute inset-0 ring-2 ring-inset ring-terracotta" />}
                  </button>
                ))}
                <button type="button" onClick={() => setColorGridOpen(true)} data-testid="view-all-colors"
                  className="shrink-0 rounded-md border border-dashed border-limestone px-3 py-2 text-xs font-medium text-terracotta transition-colors hover:border-terracotta">
                  View all {colorOptions.length} colors
                </button>
              </div>
            </div>
          )}

          {formats.length > 1 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="format-switcher">
              {formats.map((f) => (
                <button
                  key={f.id}
                  onClick={() => !f.current && navigate(`/dealer/discover/${f.id}`)}
                  disabled={f.current}
                  data-testid={`format-pill-${f.sku}`}
                  className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
                    f.current
                      ? "border-terracotta bg-terracotta text-white"
                      : "border-border bg-white text-charcoal hover:border-terracotta hover:text-terracotta"
                  }`}
                >
                  {item.category === "mosaic" ? f.sku : FORMAT_LABEL[f.format_in] || f.format_in}
                </button>
              ))}
            </div>
          )}

          <p className="mt-3 font-mono text-xs text-muted-foreground" data-testid="packaging-line">
            {item.category === "mosaic" ? (<>
              {item.sku} · {item.mesh_sheet_approx ? `Mesh sheet ${item.mesh_sheet_approx}` : "Mesh sheet size not set"}
              {item.sqft_per_sheet != null ? ` · ${item.sqft_per_sheet} sq ft/sheet` : ""} · {item.sheets_per_box ? `${item.sheets_per_box} sheets/box` : "sheets/box not set"} · {item.box_coverage_sqft} sq ft/box
            </>) : (<>
              {item.sku} · {dimsLine(item)} · {item.presentation}
              {item.pieces_per_box ? ` · ${item.pieces_per_box} pcs/box` : ""} · {item.box_coverage_sqft} sqft/box
            </>)}
            {item.boxes_per_pallet ? ` · ${item.boxes_per_pallet} boxes/pallet` : ""}
          </p>

          <div className="mt-6 space-y-3 rounded-md border border-border bg-white p-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{customerView ? "Price" : "My selling price ($/sq ft)"}</span>
              {customerView ? (
                item.retail_price_per_sqft != null ? (
                  <span className="text-sm text-charcoal" data-testid="detail-retail-per-sqft">
                    <span className="num text-lg font-semibold">${item.retail_price_per_sqft.toFixed(2)}</span>
                    <span className="text-xs text-muted-foreground">/sq ft</span>
                    <span className="ml-1.5 text-xs text-muted-foreground">(${item.retail_price.toFixed(2)}/box)</span>
                  </span>
                ) : (
                  <span className="text-sm font-medium text-charcoal">{item.retail_price != null ? `$${item.retail_price.toFixed(2)}` : "—"}</span>
                )
              ) : retailEdit != null ? (
                <form onSubmit={(e) => { e.preventDefault(); handleSaveRetail(); }} className="flex flex-col items-end gap-1" data-testid="retail-price-form">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">$</span>
                    <Input type="number" step="0.01" min="0" value={retailEdit} onChange={(e) => setRetailEdit(e.target.value)} className="h-9 w-28 bg-white focus-visible:ring-terracotta" data-testid="retail-price-input" aria-label={item.box_coverage_sqft ? "Selling price per square foot" : "Selling price per box"} />
                    <span className="text-xs text-muted-foreground">{item.box_coverage_sqft ? "/sq ft" : "/box"}</span>
                    <Button size="sm" type="submit" disabled={saving} data-testid="retail-price-save" className="bg-terracotta text-white hover:bg-terracotta-hover">Save</Button>
                  </div>
                  {item.box_coverage_sqft ? (
                    <p className="text-[11px] text-muted-foreground" data-testid="retail-price-box-preview">= ${retailEdit && !isNaN(parseFloat(retailEdit)) ? (parseFloat(retailEdit) * item.box_coverage_sqft).toFixed(2) : "0.00"}/box · {item.box_coverage_sqft} sq ft per box</p>
                  ) : null}
                </form>
              ) : (
                <button onClick={() => setRetailEdit(String((item.box_coverage_sqft ? item.retail_price_per_sqft : item.retail_price) ?? ""))} className="flex items-center gap-2 text-sm font-medium text-charcoal hover:text-terracotta" data-testid="retail-price-edit">
                  {item.retail_price != null
                    ? (item.retail_price_per_sqft != null
                      ? <span data-testid="detail-retail-per-sqft"><span className="num text-lg font-semibold">${item.retail_price_per_sqft.toFixed(2)}</span><span className="text-xs font-normal text-muted-foreground">/sq ft</span> <span className="text-xs font-normal text-muted-foreground">(${item.retail_price.toFixed(2)}/box)</span>{item.retail_price_source && <span className="ml-2 text-[10px] font-normal uppercase tracking-wide text-warmgrey" data-testid="detail-retail-source">{item.retail_price_source === "format_default" ? `${item.format_in || "Format"} default` : "Variant price"}</span>}</span>
                      : `$${item.retail_price.toFixed(2)}`)
                    : "Set price per square foot"}
                  <Pencil className="h-3 w-3 text-muted-foreground" />
                </button>
              )}
            </div>
            {!customerView && <p className="text-[11px] leading-relaxed text-muted-foreground" data-testid="retail-price-guidance">Enter the price you charge your customers per square foot. Zelligio Connect automatically calculates the selling price per box.</p>}
            {!customerView && (
              <>
                <div className="flex justify-between border-t border-border pt-3" data-testid="detail-trade-price-row">
                  <span className="text-sm text-muted-foreground">Your trade price</span>
                  {item.quote_required ? (
                    <span className="text-xs font-medium text-terracotta" data-testid="detail-quote-required">{item.quote_message || "Special Finish — Contact Us for Pricing"}</span>
                  ) : item.trade_price_per_sqft != null ? (
                    <span className="text-sm text-charcoal" data-testid="detail-trade-per-sqft">
                      <span className="num text-lg font-semibold">${item.trade_price_per_sqft.toFixed(2)}</span>
                      <span className="text-xs text-muted-foreground">/sq ft</span>
                      {item.trade_price != null && <span className="ml-1.5 text-xs text-muted-foreground">(${item.trade_price.toFixed(2)}/box)</span>}
                    </span>
                  ) : (
                    <span className="text-sm text-charcoal">{item.trade_price != null ? `$${item.trade_price.toFixed(2)}` : "—"}</span>
                  )}
                </div>
                {item.margin_pct != null && (
                  <div className="flex justify-between" data-testid="detail-margin-row">
                    <span className="text-sm text-muted-foreground">{item.retail_price != null ? "Gross margin" : "Potential gross margin"}{item.gross_profit_per_sqft != null ? ` · $${item.gross_profit_per_sqft.toFixed(2)}/sq ft profit` : ""}</span>
                    <span className="text-sm font-medium text-status-available">{item.margin_pct}%</span>
                  </div>
                )}
              </>
            )}
          </div>

          <Button
            variant="outline"
            onClick={() => window.dispatchEvent(new CustomEvent("copilot:open", { detail: { kind: "variant", id, label: item.label } }))}
            data-testid="ask-copilot-product-button"
            className="mt-4 h-11 w-full border-limestone text-charcoal transition-all duration-200 hover:-translate-y-[1px] hover:bg-sand"
          >
            Ask ZELI about this product
          </Button>

          {/* Selling actions — one cluster: save, sample, and (when sellable) hold/buy */}
          <div className="mt-4 grid grid-cols-2 gap-3" data-testid="action-cluster">
            <Button
              variant="outline"
              onClick={() => setSaveOpen(true)}
              data-testid="save-to-project-button"
              className="h-11 border-limestone text-charcoal transition-all duration-200 hover:-translate-y-[1px] hover:bg-sand"
            >
              <FolderPlus className="mr-2 h-4 w-4" /> Save to project
            </Button>
            <Button
              variant="outline"
              onClick={() => setSampleOpen(true)}
              data-testid="request-sample-button"
              className="h-11 border-terracotta text-terracotta transition-all duration-200 hover:-translate-y-[1px] hover:bg-[#BC6955]/5"
            >
              <FlaskConical className="mr-2 h-4 w-4" /> Request sample
            </Button>
            {item.florida.available > 0 && canTrade && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setReserveTarget({ florida: true })}
                  data-testid="reserve-florida-button"
                  className="h-11 border-terracotta text-terracotta hover:bg-[#BC6955]/5"
                >
                  Reserve stock
                </Button>
                <Button
                  onClick={() => setOrderPrefill({ variant_id: id, label: item.label })}
                  data-testid="buy-florida-button"
                  className="h-11 bg-terracotta text-white transition-all duration-200 hover:-translate-y-[1px] hover:bg-terracotta-hover"
                >
                  <ShoppingBag className="mr-2 h-4 w-4" /> Buy now
                </Button>
              </>
            )}
          </div>

          <div className="mt-6 space-y-4">
            {item.quote_required && (
              <Card className="border-terracotta/40 bg-[#BC6955]/5" data-testid="quote-required-banner">
                <CardContent className="flex items-center gap-3 p-4">
                  <p className="text-sm font-medium text-terracotta">{item.quote_message || "Special Finish — Contact Us for Pricing"}</p>
                </CardContent>
              </Card>
            )}

            {/* Availability & fulfillment — one coherent story (founder rule J) */}
            <Card className="border-border bg-white" data-testid="availability-section">
              <CardContent className="space-y-4 p-6">
                {item.florida.available > 0 ? (
                  <div data-testid="layer-florida">
                    <div className="flex items-center gap-3">
                      <Warehouse className="h-4 w-4 text-terracotta" />
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Available now</p>
                    </div>
                    <div className="mt-3 flex items-end gap-3">
                      <p className="font-display text-3xl font-light text-charcoal" data-testid="florida-available">{item.florida.available}</p>
                      <p className="pb-1 text-xs text-muted-foreground">boxes{item.box_coverage_sqft ? ` (≈${Math.round(item.florida.available * item.box_coverage_sqft).toLocaleString()} sq ft)` : ""} in our Florida warehouse · typically ships in 2–5 business days</p>
                    </div>
                    {!customerView && (item.florida.reserved > 0 || item.florida.committed > 0) && (
                      <p className="mt-2 text-xs text-muted-foreground" data-testid="florida-held-line">
                        {item.florida.reserved} on hold · {item.florida.committed} sold
                      </p>
                    )}
                    {item.in_transit.length > 0 && (
                      <p className="mt-2 text-xs text-muted-foreground" data-testid="incoming-secondary">
                        + {item.in_transit.reduce((a, t) => a + t.available_boxes, 0)} incoming boxes available to pre-reserve
                        {item.in_transit[0]?.eta_warehouse ? ` · est. warehouse availability ${formatDateOnly(item.in_transit[0].eta_warehouse)}` : ""}
                      </p>
                    )}
                  </div>
                ) : item.in_transit.length > 0 ? (
                  <div data-testid="layer-intransit">
                    <div className="flex items-center gap-3">
                      <Ship className="h-4 w-4 text-terracotta" />
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Incoming</p>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground" data-testid="florida-unavailable">Not currently in Florida</p>
                    <div className="mt-4 space-y-3">
                      {item.in_transit.map((t) => (
                        <div key={t.container_id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-background p-4" data-testid={`intransit-${t.container_number}`}>
                          <div>
                            <p className="text-sm font-medium text-charcoal">
                              {t.status === "DEPARTED" ? "At sea" : "Arriving soon"}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Est. warehouse availability: {t.eta_warehouse ? formatDateOnly(t.eta_warehouse) : "to be confirmed"}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="badge-status badge-reserved">{t.available_boxes} incoming</span>
                            {t.pre_reservation_enabled && canTrade && t.available_boxes > 0 && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setReserveTarget({ container_id: t.container_id, container_number: t.container_number })}
                                  data-testid={`reserve-incoming-${t.container_number}`}
                                  className="border-limestone text-charcoal hover:bg-sand"
                                >
                                  Reserve incoming
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setOrderPrefill({ variant_id: id, label: item.label, container_id: t.container_id, container_number: t.container_number })}
                                  data-testid={`buy-incoming-${t.container_number}`}
                                  className="border-terracotta text-terracotta hover:bg-[#BC6955]/5"
                                >
                                  Buy incoming
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">Incoming stock can be pre-reserved while at sea — holds become active when it arrives, with the same expiry.</p>
                    {!customerView && !item.quote_required && (
                      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="notify-me-intransit">
                        <NotifyMeButton variant={item} active={notifyMe.active.has(item.id)} onClick={() => setAvailOpen(true)} />
                        <span className="text-xs text-muted-foreground">Get alerted when stock is actually orderable in Florida.</span>
                      </div>
                    )}
                  </div>
                ) : item.morocco.available > 0 ? (
                  <div data-testid="layer-morocco">
                    <div className="flex items-center gap-3">
                      <MapPin className="h-4 w-4 text-terracotta" />
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Available at origin</p>
                    </div>
                    <div className="mt-3 flex items-end gap-3">
                      <p className="font-display text-3xl font-light text-charcoal" data-testid="morocco-available">{item.morocco.available}</p>
                      <p className="pb-1 text-xs text-muted-foreground">boxes at our Morocco atelier — special order, lead time applies</p>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground" data-testid="florida-unavailable">Not currently in Florida</p>
                    {!customerView && !item.quote_required && (
                      <div className="mt-3" data-testid="notify-me-morocco"><NotifyMeButton variant={item} active={notifyMe.active.has(item.id)} onClick={() => setAvailOpen(true)} /></div>
                    )}
                  </div>
                ) : (
                  <div data-testid="layer-request-availability">
                    <p className="text-sm font-medium text-charcoal">Not currently available</p>
                    <p className="mt-1 text-sm text-warmgrey">No Florida, incoming, or origin stock on record for this variant.</p>
                    {!customerView && !item.quote_required && (
                      <div className="mt-4"><NotifyMeButton variant={item} active={notifyMe.active.has(item.id)} onClick={() => setAvailOpen(true)} size="default" /></div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <ProductResources />

      <Dialog open={colorGridOpen} onOpenChange={setColorGridOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-border bg-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">All {colorOptions.length} colors</DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">Select a color — your current format is kept when available.</DialogDescription>
          </DialogHeader>
          <Input value={colorQuery} onChange={(e) => setColorQuery(e.target.value)} placeholder="Search by MZ code or color name"
            data-testid="color-grid-search" className="h-11 bg-white focus-visible:ring-terracotta" />
          <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="color-grid">
            {colorOptions
              .filter((c) => `${c.code} ${c.name}`.toLowerCase().includes(colorQuery.trim().toLowerCase()))
              .map((c) => (
                <button key={c.code} type="button"
                  onClick={() => { setColorGridOpen(false); if (c.variant_id !== item.id) navigate(`/dealer/discover/${c.variant_id}`); }}
                  data-testid={`color-grid-${c.code}`}
                  className={`rounded-md border p-3 text-left transition-colors ${c.code === item.color_code ? "border-terracotta bg-[#BC6955]/5" : "border-border bg-white hover:border-terracotta"}`}>
                  <p className="font-mono text-xs text-terracotta">{c.code}</p>
                  <p className="mt-0.5 truncate text-xs font-medium text-charcoal">{c.name}</p>
                </button>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      <NotifyMeDialog variant={item} open={availOpen} onOpenChange={setAvailOpen} projects={projects} onSubmitted={() => notifyMe.add(item.id)} />

      <BackToTop />

      <Dialog open={!!reserveTarget} onOpenChange={(o) => !o && setReserveTarget(null)}>
        <DialogContent className="border-border bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium text-charcoal">
              Reserve {reserveTarget?.florida ? "available stock" : "incoming stock"}
            </DialogTitle>
            <DialogDescription className="text-sm text-warmgrey">
              First holds are confirmed for 72 hours automatically. A repeat hold for the same variant and project requires Zelligio approval and does not hold stock while pending.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleReserve} className="mt-2 space-y-5" data-testid="reserve-form">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Boxes *</Label>
              <Input
                required
                type="number"
                min="1"
                max={reserveTarget?.florida ? item.florida.available : reserveTarget ? item.in_transit.find((t) => t.container_id === reserveTarget.container_id)?.available_boxes : undefined}
                data-testid="reserve-boxes-input"
                value={reserveBoxes}
                onChange={(e) => setReserveBoxes(e.target.value)}
                className="h-11 bg-white focus-visible:ring-terracotta"
              />
            </div>
            {!reserveProject && showLocationPicker && (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Business location</Label>
                <Select value={reserveLocationId || defaultLocationId || ""} onValueChange={setReserveLocationId}>
                  <SelectTrigger data-testid="reserve-location-select" className="h-11 bg-white"><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {dealerLocations.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.label || l.type} — {l.address?.city}{l.address?.state ? `, ${l.address.state}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Project (optional)</Label>
              <Select value={reserveProject} onValueChange={setReserveProject}>
                <SelectTrigger data-testid="reserve-project-select" className="h-11 bg-white"><SelectValue placeholder="No project" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={reserving} data-testid="reserve-submit" className="h-11 w-full bg-terracotta text-white hover:bg-terracotta-hover">
              {reserving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm reservation"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <SampleRequestDialog
        open={sampleOpen}
        onOpenChange={setSampleOpen}
        catalog={[{ ...item, id }]}
        projects={projects}
        preselectedVariantId={id}
        onSubmitted={load}
        onProjectCreated={() => api.get("/projects").then((res) => setProjects(res.data)).catch(() => {})}
      />

      <OrderSheetDialog
        open={!!orderPrefill}
        onOpenChange={(o) => !o && setOrderPrefill(null)}
        prefillLine={orderPrefill}
        onCreated={(order) => navigate(`/dealer/orders/${order.id}`)}
      />

      <SaveToProjectDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        variant={{ id, label: item.label }}
      />
    </div>
  );
}
